import { LeftOutlined, RightOutlined, SettingOutlined } from "@ant-design/icons";
import { Avatar, Button, Divider, Row, Skeleton, Tooltip } from "antd";
import { TriggerModal } from "components/triggerModal";
import { useFetchCreditorPortalDebtorProfileQuery } from "features/accountsTable/creditorPortal/accountsTableAPI";
import TableAppearanceModal, {
  reconcileColumns,
} from "features/appearance/components/tableAppearanceModal";
import { SIDER_ITEMS } from "features/appearance/constants";
import { DebtorAddressInfoCard } from "features/debtorSider/components/debtorAddressInfoCard";
import { DebtorContactInfoCard } from "features/debtorSider/components/debtorContactInfoCard";
import { DebtorEmailInfoCard } from "features/debtorSider/components/debtorEmailInfoCard";
import { DebtorEmploymentInfoCard } from "features/debtorSider/components/debtorEmploymentInfoCard";
import { DebtorInfoCard } from "features/debtorSider/components/debtorInfoCard";
import { DebtorPhoneInfoCard } from "features/debtorSider/components/debtorPhoneInfoCard";
import {
  useFetchCreditorPortalDebtorAddressesQuery,
  useFetchCreditorPortalDebtorContactsQuery,
  useFetchCreditorPortalDebtorEmailsQuery,
  useFetchCreditorPortalDebtorEmploymentsQuery,
  useFetchCreditorPortalDebtorPhoneNumbersQuery,
} from "features/debtorSider/creditorPortal/debtorSiderAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useAppearance } from "providers/appearanceProvider";
import { useParams } from "react-router-dom";
import styled from "styled-components";

const StyledSiderPanel = styled.div`
  position: fixed;
  top: 64px;
  left: 0;
  bottom: 0;
  width: 400px;
  background: #fff;
  display: flex;
  flex-direction: column;
  z-index: 9;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  transform: translateX(${({ $collapsed }) => ($collapsed ? "-400px" : "0")});
  transition: transform 0.25s ease;
`;

const StyledSiderScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px;
`;

const StyledCollapseTab = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  left: ${({ $collapsed }) => ($collapsed ? "0" : "400px")};
  transition: left 0.25s ease;
  z-index: 10;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-left: none;
  border-radius: 0 6px 6px 0;
  padding: 14px 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);

  &:hover {
    background: #f5f5f5;
  }

  .anticon {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
  }
`;

const StyledSiderHeader = styled.div`
  flex-shrink: 0;
  background: #fff;
`;

const StyledHeaderName = styled.h4`
  margin: 2px 0 2px 8px;
`;

const StyledProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px 8px;
`;

const StyledAvatar = styled(Avatar)`
  background-color: #0b3948;
`;

const StyledDebtorName = styled.h2`
  text-align: center;
  text-transform: capitalize;
  margin-left: 8px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 220px;
`;

const StyledDivider = styled(Divider)`
  margin-bottom: 0;
  margin-top: 11px;
`;

const StyledCompactDivider = styled(Divider)`
  margin: 8px 0;
`;

const StyledEmptySection = styled.div`
  display: flex;
  align-items: center;
  margin-left: 8px;
  gap: 8px;
  color: rgba(0, 0, 0, 0.45);
`;

export const SIDER_ID = "debtorSider";

function DebtorSider({ isSiderCollapsed, onToggleCollapse }) {
  const { debtorId } = useParams();
  const {
    data: debtorPersonalInfo,
    isLoading: isDebtorPersonalInfoLoading,
    isError: isDebtorPersonalInfoError,
  } = useFetchCreditorPortalDebtorProfileQuery({ debtorId });
  const { data: debtorAddresses, isLoading: isDebtorAddressesLoading } =
    useFetchCreditorPortalDebtorAddressesQuery(
      { debtorId },
      { skip: isDebtorPersonalInfoLoading || isDebtorPersonalInfoError },
    );
  const { data: debtorContacts, isLoading: isDebtorContactsLoading } =
    useFetchCreditorPortalDebtorContactsQuery(
      {
        debtorId,
      },
      { skip: isDebtorPersonalInfoLoading || isDebtorPersonalInfoError },
    );
  const { data: debtorPhoneNumbers, isLoading: isDebtorPhoneNumbersLoading } =
    useFetchCreditorPortalDebtorPhoneNumbersQuery(
      { debtorId },
      { skip: isDebtorPersonalInfoLoading || isDebtorPersonalInfoError },
    );
  const { data: debtorEmails, isLoading: isDebtorEmailsLoading } =
    useFetchCreditorPortalDebtorEmailsQuery(
      {
        debtorId,
      },
      { skip: isDebtorPersonalInfoLoading || isDebtorPersonalInfoError },
    );
  const { data: debtorEmployments, isLoading: isDebtorEmploymentsLoading } =
    useFetchCreditorPortalDebtorEmploymentsQuery(
      { debtorId },
      { skip: isDebtorPersonalInfoLoading || isDebtorPersonalInfoError },
    );
  const { isLoading: isAppearanceLoading, appearance } = useAppearance();

  const {
    data: {
      [PERMISSIONS.DEBTOR_ADDRESS__GET]: isGetDebtorAddressAuthorized,
      [PERMISSIONS.DEBTOR_CONTACT__GET]: isGetDebtorContactAuthorized,
      [PERMISSIONS.DEBTOR_EMPLOYMENT__GET]: isGetDebtorEmploymentAuthorized,
      [PERMISSIONS.DEBTOR_PHONE_NUMBER__GET]: isGetDebtorPhoneNumberAuthorized,
      [PERMISSIONS.DEBTOR_EMAIL__GET]: isGetDebtorEmailAuthorized,
      [PERMISSIONS.APPEARANCE__UPDATE]: isAppearanceUpdateAuthorized,
    },
  } = useAuthorizations();

  const isLoading =
    isDebtorPersonalInfoLoading ||
    isDebtorAddressesLoading ||
    isDebtorContactsLoading ||
    isDebtorPhoneNumbersLoading ||
    isDebtorEmploymentsLoading ||
    isDebtorEmailsLoading ||
    isAppearanceLoading;

  const debtorDisplayName = [
    debtorPersonalInfo?.firstName,
    debtorPersonalInfo?.middleName,
    debtorPersonalInfo?.lastName,
  ]
    .filter((x) => x)
    .join(" ");

  const defaultItems = [
    {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_INFO,
      visible: true,
      title: "Debtor Info",
      render: () => <DebtorInfoCard profileType={debtorPersonalInfo?.type} alwaysExpanded />,
    },
    isGetDebtorPhoneNumberAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__PHONE_NUMBERS,
      visible: true,
      title: "Phone Numbers",
      render: (item) => (
        <span key={item.key}>
          {debtorPhoneNumbers?.map((phone, index) => (
            <DebtorPhoneInfoCard
              key={index}
              phone={phone}
              profileType={debtorPersonalInfo?.type}
              title="Phone"
              isFirstItem={index === 0}
            />
          ))}
          {debtorPhoneNumbers?.length === 0 && (
            <>
              <StyledHeaderName>Phones</StyledHeaderName>
              <StyledEmptySection>There are currently no phone numbers.</StyledEmptySection>
            </>
          )}
        </span>
      ),
    },
    isGetDebtorEmailAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_EMAILS,
      visible: true,
      title: "Emails",
      render: (item) => (
        <span key={item.key}>
          {debtorEmails?.map((email, index) => (
            <DebtorEmailInfoCard
              key={email.id}
              profileType={debtorPersonalInfo?.type}
              email={email}
              title="Email"
              isFirstItem={index === 0}
            />
          ))}
          {debtorEmails?.length === 0 && (
            <>
              <StyledHeaderName>Email</StyledHeaderName>
              <StyledEmptySection>There are currently no emails.</StyledEmptySection>
            </>
          )}
        </span>
      ),
    },
    isGetDebtorAddressAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_ADDRESSES,
      visible: true,
      title: "Addresses",
      render: (item) => (
        <span key={item.key}>
          {debtorAddresses?.map((address, index) => (
            <DebtorAddressInfoCard
              key={index}
              profileType={debtorPersonalInfo?.type}
              address={address}
              title="Address"
              isFirstItem={index === 0}
            />
          ))}
          {debtorAddresses?.length === 0 && (
            <>
              <StyledHeaderName>Address</StyledHeaderName>
              <StyledEmptySection>There are currently no addresses.</StyledEmptySection>
            </>
          )}
        </span>
      ),
    },

    isGetDebtorContactAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_CONTACTS,
      visible: true,
      title: "Contacts",
      render: (item) => {
        const filteredContacts =
          debtorContacts?.filter((contact) => contact.relationshipToDebtor !== "debtor") ?? [];
        const contactLabel =
          debtorPersonalInfo?.type === "consumer" ? "Debtor Contact" : "Contact Person";
        return (
          <span key={item.key}>
            {filteredContacts.map((debtorContact, index) => (
              <DebtorContactInfoCard
                key={index}
                profileType={debtorPersonalInfo?.type}
                debtorContact={debtorContact}
                title={contactLabel}
                isFirstItem={index === 0}
              />
            ))}
            {filteredContacts.length === 0 && (
              <>
                <StyledHeaderName>{contactLabel}</StyledHeaderName>
                <StyledEmptySection>
                  There are currently no {contactLabel.toLowerCase()}s.
                </StyledEmptySection>
              </>
            )}
          </span>
        );
      },
    },
    isGetDebtorEmploymentAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_EMPLOYMENTS,
      visible: true,
      title: "Employments",
      render: (item) => (
        <span key={item.key}>
          {debtorPersonalInfo?.type === "consumer" &&
            debtorEmployments?.map((employment, index) => (
              <DebtorEmploymentInfoCard
                key={index}
                employment={employment}
                title="Employment"
                isFirstItem={index === 0}
              />
            ))}
          {debtorPersonalInfo?.type === "consumer" && debtorEmployments?.length === 0 && (
            <>
              <StyledHeaderName>Employment</StyledHeaderName>
              <StyledEmptySection>There are currently no employments.</StyledEmptySection>
            </>
          )}
        </span>
      ),
    },
  ].filter(Boolean);

  const reconciledItems = reconcileColumns(defaultItems, appearance?.items?.[SIDER_ID]).filter(
    (column) => column.visible,
  );

  if (
    appearance?.items?.[SIDER_ID]?.hidePanel ||
    isAppearanceLoading ||
    isDebtorPersonalInfoError
  ) {
    return null;
  }

  const sharedProfileHeader = !isDebtorPersonalInfoLoading && !isDebtorPersonalInfoError && (
    <StyledProfileContainer>
      <Row align="middle" wrap={false}>
        <StyledAvatar size={40}>
          {(debtorDisplayName || debtorPersonalInfo?.companyName)?.split(" ").map((c) => c[0])}
        </StyledAvatar>
        <Tooltip title={debtorDisplayName || debtorPersonalInfo?.companyName} placement="right">
          <StyledDebtorName>
            {(debtorDisplayName || debtorPersonalInfo?.companyName)?.toLowerCase()}
          </StyledDebtorName>
        </Tooltip>
        {isAppearanceUpdateAuthorized && (
          <TriggerModal
            modal={TableAppearanceModal}
            tableId={SIDER_ID}
            defaultColumns={defaultItems}
            mode="sider"
          >
            <Tooltip title="Customize Appearance">
              <Button shape="circle" icon={<SettingOutlined />} type="text" />
            </Tooltip>
          </TriggerModal>
        )}
      </Row>
    </StyledProfileContainer>
  );

  const sharedContent = reconciledItems.map((item, idx) => (
    <div key={idx}>
      {item.render(item)}
      <StyledCompactDivider />
    </div>
  ));

  return (
    <>
      <StyledCollapseTab
        $collapsed={isSiderCollapsed}
        onClick={onToggleCollapse}
        title={isSiderCollapsed ? "Expand panel" : "Collapse panel"}
      >
        {isSiderCollapsed ? <RightOutlined /> : <LeftOutlined />}
      </StyledCollapseTab>

      <StyledSiderPanel $collapsed={isSiderCollapsed}>
        <StyledSiderHeader>
          {sharedProfileHeader}
          <StyledDivider />
        </StyledSiderHeader>

        <StyledSiderScrollArea>
          {isLoading ? <Skeleton active /> : sharedContent}
        </StyledSiderScrollArea>
      </StyledSiderPanel>
    </>
  );
}

export default DebtorSider;
